import { memo, useMemo } from 'react';
import { batch, shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { ISpecOffer } from '@r1-frontend/api-domru/spec-offer/v1/spec-offers';

import SpeedBonusConnection from '~/src/entities/speed-bonus';
import SpecialOfferPopup from '~/src/features/payments-abn/components/SpecialOffer/SpecialOfferPopup';
import { toggleIsShowSpecOffer } from '~/src/features/payments-abn/store/actionCreators';
import { specialOfferAnalytics } from '~/src/features/specialOffersContainer/analytics';
import { DefaultCallback } from '~/src/features/specialOffersContainer/constants';
import LkPromotionalPopup from '~/src/features/specialOffersContainer/containers/LkPromotionalPopup';
import {
    PromoConnectedPopup,
    PromoConnectionPopup,
    PromoErrorPopup,
    PromoNeedPaymentPopup,
    PromoNeedSetPhonePopup,
} from '~/src/features/specialOffersContainer/containers/popups';
import SpecOfferDetails from '~/src/features/specialOffersContainer/containers/SpecOfferDetails';
import { isNewDetailsDesign } from '~/src/features/specialOffersContainer/helpers';
import { useCallbackMap, useConnectOffer } from '~/src/features/specialOffersContainer/hooks';
import { setIsPopupOpened, setPopupState, setSelectedOfferId } from '~/src/features/specialOffersContainer/store/actions';
import { viewOffer } from '~/src/features/specialOffersContainer/store/actions/thunk';
import { selectNewOrViewedOffers, selectOffer } from '~/src/features/specialOffersContainer/store/selectors';
import { TState } from '~/src/store';

const SpecialOffer = (): JSX.Element => {
    const dispatch = useDispatch();

    const {
        specOffers: { isPopupOpened, popupState },
        popupOffer,
        stoppers: { stopper },
    } = useSelector((state: TState) => ({
        specOffers: state.specOffers,
        popupOffer: selectOffer(state),
        stoppers: selectNewOrViewedOffers(state),
    }), shallowEqual);

    const router = useRouter<string>();

    const { referrer = '/lk' } = router.query;

    const callbackMap = useCallbackMap('lk');

    const { activateButtonText, connectOffer } = useConnectOffer(callbackMap, stopper);

    const isNewOfferDetails = useMemo(
        () => popupOffer?.isShowNewDetail && isNewDetailsDesign(),
        [popupOffer?.isShowNewDetail],
    );

    const connectHandler = () => {
        dispatch(toggleIsShowSpecOffer(false));

        if (!stopper?.hasInstantConnect) {
            specialOfferAnalytics.showPromoDetails(stopper);
            dispatch(viewOffer(stopper, false));
            return;
        }

        dispatch(setSelectedOfferId(stopper?.requestId));

        if (!stopper?.navigation?.type) {
            specialOfferAnalytics.instantConnect(stopper);

            switch (true) {
                case !!stopper.paySum:
                    dispatch(setPopupState('needPayment'));
                    return;
                case stopper.billingState !== 'connected' && (stopper.benefits?.some(({ needPhone }) => needPhone) ?? false):
                    dispatch(setPopupState('needSetPhone'));
                    return;
                default:
                    dispatch(setPopupState('needConfirmation'));
                    return;
            }
        }

        connectOffer();
    };

    const handleRedirect = () => {
        router.push(referrer);
    };

    const onClosePopup = () => {
        batch(() => {
            dispatch(setPopupState('none'));
            dispatch(setIsPopupOpened(false));
        });

        handleRedirect();
    };

    const promoPopupProps = {
        isPopupOpen: isPopupOpened,
        offer: popupOffer,
        callbackMap: callbackMap,
        onClose: onClosePopup,
    };

    return (
        <SpeedBonusConnection>
            <SpecialOfferPopup
                buttonText={activateButtonText}
                onConnect={connectHandler}
                onClose={handleRedirect}
            />

            {isNewOfferDetails
                ? <SpecOfferDetails {...promoPopupProps}/>
                : <LkPromotionalPopup {...promoPopupProps}/>
            }

            <PromoConnectedPopup
                isOpen={popupState === 'connected'}
                offer={popupOffer || ({} as ISpecOffer)}
                onClose={onClosePopup}
            />

            <PromoErrorPopup
                isOpen={popupState === 'connectionError'}
                onClose={onClosePopup}
            />

            <PromoNeedPaymentPopup
                isOpen={popupState === 'needPayment'}
                offer={popupOffer || ({} as ISpecOffer)}
                onClose={onClosePopup}
            />

            <PromoNeedSetPhonePopup
                isOpen={popupState === 'needSetPhone'}
                offer={popupOffer || ({} as ISpecOffer)}
                onActivate={callbackMap[DefaultCallback]}
                onClose={onClosePopup}
            />

            <PromoConnectionPopup
                isPopupOpen={popupState === 'needConfirmation'}
                offer={popupOffer}
                callbackMap={callbackMap}
                onClose={onClosePopup}
            />
        </SpeedBonusConnection>
    );
};

export default memo(SpecialOffer);
